import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, Mail, AlertTriangle, DollarSign } from 'lucide-react';

const Subscriptions = () => {
  const [email, setEmail] = useState('');
  const [subscriptions, setSubscriptions] = useState([
    {
      id: 1,
      name: 'Netflix',
      icon: '📺',
      amount: 15.99,
      billingDate: '2024-03-15',
      category: 'Entertainment',
      yearlyTotal: 191.88
    },
    {
      id: 2,
      name: 'Spotify',
      icon: '🎵',
      amount: 9.99,
      billingDate: '2024-03-20',
      category: 'Music',
      yearlyTotal: 119.88
    },
    {
      id: 3,
      name: 'Amazon Prime',
      icon: '📦',
      amount: 14.99,
      billingDate: '2024-03-05',
      category: 'Shopping',
      yearlyTotal: 179.88
    }
  ]);

  const [showConfirmation, setShowConfirmation] = useState<number | null>(null);

  const handleSwipe = (id: number, action: 'keep' | 'cancel') => {
    if (action === 'cancel') {
      setShowConfirmation(id);
    }
  };

  const confirmCancellation = (id: number) => {
    setSubscriptions(subscriptions.filter(sub => sub.id !== id));
    setShowConfirmation(null);
  };

  return (
    <div className="space-y-6">
      {/* Email Input */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/50 dark:bg-gray-800/50 backdrop-blur-md rounded-2xl p-6 shadow-xl"
      >
        <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-200 mb-6">
          Connect Your Subscriptions
        </h2>
        <div className="flex gap-4">
          <div className="flex-1">
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white/50 dark:bg-gray-800/50 focus:outline-none focus:ring-2 focus:ring-[#68A511]"
              />
            </div>
          </div>
          <button className="px-6 py-2 bg-[#68A511] text-white rounded-lg hover:bg-[#598615] transition-colors">
            Connect
          </button>
        </div>
      </motion.div>

      {/* Subscriptions List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="space-y-4"
      >
        {subscriptions.map((subscription) => (
          <motion.div
            key={subscription.id}
            layout
            className="bg-white/50 dark:bg-gray-800/50 backdrop-blur-md rounded-2xl p-6 shadow-xl relative overflow-hidden"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="text-4xl">{subscription.icon}</span>
                <div>
                  <h3 className="font-semibold text-gray-700 dark:text-gray-200">
                    {subscription.name}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Next billing: {new Date(subscription.billingDate).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xl font-semibold text-[#68A511]">
                  ${subscription.amount}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  per month
                </p>
              </div>
            </div>

            {/* Swipe Actions */}
            <div className="flex gap-4 mt-4">
              <button
                onClick={() => handleSwipe(subscription.id, 'keep')}
                className="flex-1 py-2 px-4 bg-[#DCE9C2] text-[#598615] rounded-lg hover:bg-[#68A511] hover:text-white transition-colors flex items-center justify-center gap-2"
              >
                <Check size={20} />
                Keep
              </button>
              <button
                onClick={() => handleSwipe(subscription.id, 'cancel')}
                className="flex-1 py-2 px-4 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors flex items-center justify-center gap-2"
              >
                <X size={20} />
                Cancel
              </button>
            </div>

            {/* Yearly Savings Info */}
            <div className="mt-4 p-4 bg-[#DCE9C2] rounded-lg">
              <p className="text-[#598615] flex items-center gap-2">
                <DollarSign size={20} />
                Yearly cost: ${subscription.yearlyTotal}
              </p>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Confirmation Modal */}
      <AnimatePresence>
        {showConfirmation && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full"
            >
              <div className="flex items-center gap-4 mb-4">
                <AlertTriangle className="text-red-500" size={24} />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Confirm Cancellation
                </h3>
              </div>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Are you sure you want to cancel this subscription? This action cannot be undone.
              </p>
              <div className="flex gap-4">
                <button
                  onClick={() => confirmCancellation(showConfirmation)}
                  className="flex-1 py-2 px-4 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                >
                  Yes, Cancel
                </button>
                <button
                  onClick={() => setShowConfirmation(null)}
                  className="flex-1 py-2 px-4 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Keep
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Subscriptions; 